import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../providers/AuthProvider";
import { ApiError } from "../services/apiClient";
import type { AsyncDataState } from "../types/async";

interface UseApiQueryOptions<T> {
  queryFn: (token: string) => Promise<T>;
  skipWhenUnauthenticated?: boolean;
  formatError?: (error: unknown) => string;
}

interface UseApiQueryResult<T> extends AsyncDataState<T> {
  refresh: () => Promise<void>;
}

export function useApiQuery<T>({
  queryFn,
  skipWhenUnauthenticated = false,
  formatError
}: UseApiQueryOptions<T>): UseApiQueryResult<T> {
  const { accessToken } = useAuth();
  const [state, setState] = useState<AsyncDataState<T>>({
    data: null,
    isLoading: true,
    error: null
  });

  const load = useCallback(async () => {
    if (!accessToken) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const data = await queryFn(accessToken);
      setState({ data, isLoading: false, error: null });
    } catch (err) {
      const message = formatError
        ? formatError(err)
        : err instanceof ApiError
          ? err.message
          : "unexpected_error";
      setState({ data: null, isLoading: false, error: message });
    }
  }, [accessToken, formatError, queryFn]);

  useEffect(() => {
    if (skipWhenUnauthenticated && !accessToken) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }
    void load();
  }, [accessToken, load, skipWhenUnauthenticated]);

  return {
    ...state,
    refresh: load
  };
}
